import { useState, useEffect, memo, useCallback } from 'react'
import { ThemeToggle } from './ui/theme-toggle'
import logo from '../assets/santsgdark.png'
import { Sparkles } from 'lucide-react'
import { Button } from './ui/button'
import { useChatVisibility } from '@/contexts/chat/ChatVisibilityContext'
import { useChatIntegration } from '@/hooks/useChatIntegration'
import { useNavigate } from 'react-router-dom'

interface NavbarProps {
  selectedDocset: string
}

function Navbar({ selectedDocset }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { isChatOpen, toggleChat } = useChatVisibility()
  const { clearMessages } = useChatIntegration()
  const navigate = useNavigate()

  const handleLogoClick = useCallback(() => {
    clearMessages()
    setIsMenuOpen(false)
    navigate('/')
  }, [clearMessages, navigate])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <nav className="sticky top-0 z-50 w-full h-[56px] flex items-center justify-between border-b border-border/20 px-4 sm:px-6 bg-background/80 backdrop-blur-md">
      <div className="flex items-center gap-3">
        <button
          onClick={handleLogoClick}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
          aria-label="Go to home"
        >
          <img
            src={logo}
            alt="SANTSG Logo"
            width="130"
            height="24"
            fetchPriority="high"
            className="h-[24px] sm:h-[28px] w-auto select-none drag-none hover:opacity-80 transition-opacity duration-200 motion-reduce:transition-none"
            onContextMenu={handleContextMenu}
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          />
        </button>
        {selectedDocset && (
          <>
            <div className="h-6 w-px bg-border/50" />
            <span className="text-sm font-medium text-muted-foreground capitalize">
              {selectedDocset}
            </span>
          </>
        )}
      </div>

      {/* Mobile & Desktop Menu */}
      <div className="flex items-center gap-2">
        {/* Ask AI Button - Always visible */}
        <Button 
          onClick={toggleChat} 
          variant="ghost"
          className={`
            h-9 px-3 text-sm gap-2 font-medium transition-all duration-300 group
            ${isChatOpen 
              ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white hover:from-violet-700 hover:via-purple-700 hover:to-indigo-700 shadow-lg' 
              : 'bg-gradient-to-r from-violet-100 via-purple-100 to-indigo-100 dark:from-violet-950 dark:via-purple-950 dark:to-indigo-950 text-violet-700 dark:text-violet-300 hover:from-violet-200 hover:via-purple-200 hover:to-indigo-200 dark:hover:from-violet-900 dark:hover:via-purple-900 dark:hover:to-indigo-900 border border-violet-300 dark:border-violet-800'
            }
          `}
        >
          <Sparkles className="h-4 w-4 group-hover:rotate-12 transition-transform duration-300" />
          <span className="hidden sm:inline">Ask AI</span>
          <kbd className={`
            hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border ml-1
            ${isChatOpen 
              ? 'bg-white/20 text-white border-white/30' 
              : 'bg-violet-200 dark:bg-violet-900 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-800'
            }
          `}>
            ⌘K
          </kbd>
        </Button>
        <div className="hidden md:flex h-9 items-center">
          <ThemeToggle />
        </div>
      </div>

    </nav>
  )
}

export default memo(Navbar)
